import React, { useEffect, useRef, useState } from "react";
import Page from "../components/Page";
import { Link } from "react-router-dom";
import { 
  IconAlertTriangle, 
  IconBox, 
  IconCarrot, 
  IconCategory2,
  IconCircleMinus, 
  IconDotsVertical, 
  IconLogs, 
  IconPencil, 
  IconPlus, 
  IconSearch, 
  IconTrash, 
  IconX,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { addInventoryItem, addInventoryItemStockMovement, deleteInventoryItem, updateInventoryItem, useInventoryItems, addBulkStockToAllItems, setBulkMinStockToAllItems } from "../controllers/inventory.controller";
import toast from "react-hot-toast";
import moment from "moment";
import { mutate } from "swr";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import { useTheme } from "../contexts/ThemeContext";

export const UNIT_LABELS = {
  kg: "Kilograms",
  g: "Grams",
  l: "Liters",
  ml: "Milliliters",
  pc: "Pieces",
};

export default function InventoryPage() {
  const { t } = useTranslation();
  const {theme} = useTheme();
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;
  const [activeActionMenuId, setActiveActionMenuId] = useState(null);

  // Clean modal state - only mount dialogs when open to avoid GPU compositor crashes
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkAddModalOpen, setIsBulkAddModalOpen] = useState(false);
  const [isBulkMinStockModalOpen, setIsBulkMinStockModalOpen] = useState(false);

  const [state, setState] = useState({
    search: '',
    inventory: [],
    statusCounts: {
      all: 0,
      in: 0,
      low: 0,
      out: 0,
    },
  });

  const titleRef = useRef(null);
  const quantityRef = useRef(null);
  const unitRef = useRef(null);
  const minQuantityThresholdRef = useRef(null);

  const updateTitleRef = useRef(null);
  const updateQuantityRef = useRef(null);
  const updateUnitRef = useRef(null);
  const updateMinQuantityThresholdRef = useRef(null);

  const [selectedUpdateItem, setSelectedUpdateItem] = useState(null);

  const [selectedDeletItem, setSelectedDeleteItem] = useState(null);

  const addStockMovementTypeRef = useRef("IN");
  const addStockMovementItemIdRef = useRef(null);
  const addStockMovementQuantityRef = useRef(null);
  const addStockMovementNoteRef = useRef(null);
  const bulkStockQuantityRef = useRef(null);
  const bulkStockNoteRef = useRef(null);
  const bulkMinStockRef = useRef(null);

  const { APIURL, data, error, isLoading } = useInventoryItems({
    status: selectedStatus
  });

  useEffect(() => {
    if (data) {
      setState((prev) => ({
        ...prev,
        inventory: data.items || [],
        statusCounts: data.statusCounts || {},
      }));
    }
  }, [data]);

  const handleAddInventoryItem = async () => {
    const title = titleRef.current?.value.trim();
    const quantity = parseFloat(String(quantityRef.current?.value || 0).replace(/,/g, ""));
    const unit = unitRef.current?.value;
    const minQuantityThreshold = parseFloat(String(minQuantityThresholdRef.current?.value || 0).replace(/,/g, ""));

    if (!title) {
      toast.error(t('inventory.toast_item_name_required'));
      return;
    }
    if (!quantity || quantity <= 0) {
      toast.error(t('inventory.toast_quantity_positive_required'));
      return;
    }
    if (!unit) {
      toast.error(t('inventory.toast_unit_required'));
      return;
    }
    if (!minQuantityThreshold || minQuantityThreshold <= 0) {
      toast.error(t('inventory.toast_min_quantity_positive_required'));
      return;
    }
    if (minQuantityThreshold > quantity) {
      toast.error(t('inventory.toast_min_quantity_less_than_stock'));
      return;
    }

    try {
      const res = await addInventoryItem({
        title,
        quantity,
        unit,
        min_quantity_threshold: minQuantityThreshold,
      });

      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);

        if (titleRef.current) titleRef.current.value = "";
        if (quantityRef.current) quantityRef.current.value = "";
        if (unitRef.current) unitRef.current.value = "pc";
        if (minQuantityThresholdRef.current) minQuantityThresholdRef.current.value = "";

        setIsAddModalOpen(false);
        try { document.getElementById('modal-add-inventory-item')?.close?.(); } catch (e) {}

        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('inventory.toast_default_error');
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  const handleUpdateInventoryItem = async () => {
    const title = updateTitleRef.current?.value.trim();
    const quantity = parseFloat(String(updateQuantityRef.current?.value || 0).replace(/,/g, ""));
    const unit = updateUnitRef.current?.value;
    const minQuantityThreshold = parseFloat(String(updateMinQuantityThresholdRef.current?.value || 0).replace(/,/g, ""));

    if(!selectedUpdateItem){
      toast.error(t('inventory.toast_invalid_request'));
      return;
    }
    if (!title) {
      toast.error(t('inventory.toast_item_name_required'));
      return;
    }
    if (!quantity || quantity <= 0) {
      toast.error(t('inventory.toast_quantity_positive_required'));
      return;
    }
    if (!unit) {
      toast.error(t('inventory.toast_unit_required'));
      return;
    }
    if (!minQuantityThreshold || minQuantityThreshold <= 0) {
      toast.error(t('inventory.toast_min_quantity_positive_required'));
      return;
    }
    if (minQuantityThreshold > quantity) {
      toast.error(t('inventory.toast_min_quantity_less_than_stock'));
      return;
    }

    try {
      const res = await updateInventoryItem({
        id: selectedUpdateItem,
        title,
        unit,
        min_quantity_threshold: minQuantityThreshold,
      });

      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);

        if (updateTitleRef.current) updateTitleRef.current.value = "";
        if (updateQuantityRef.current) updateQuantityRef.current.value = "";
        if (updateUnitRef.current) updateUnitRef.current.value = "pc";
        if (updateMinQuantityThresholdRef.current) updateMinQuantityThresholdRef.current.value = "";
        setSelectedUpdateItem(null);

        setIsUpdateModalOpen(false);
        try { document.getElementById('modal-update-inventory-item')?.close?.(); } catch (e) {}

        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('inventory.toast_default_error');
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  const handleDeleteInventoryItem = async () => {
    if(!selectedDeletItem){
      toast.error(t('inventory.toast_invalid_request'));
      return;
    }

    try {
      const res = await deleteInventoryItem({
        id: selectedDeletItem
      });

      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);

        setSelectedDeleteItem(null);

        setIsDeleteModalOpen(false);
        try { document.getElementById('modal-delete-inventory-item')?.close?.(); } catch (e) {}

        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('inventory.toast_default_error');
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  const handleAddItemStockMovement = async () => {
    const movementType = addStockMovementTypeRef.current?.value || null;
    const itemId = addStockMovementItemIdRef.current?.value || null;
    const quantity = parseFloat(String(addStockMovementQuantityRef.current?.value || "0").replace(/,/g, ""));
    const note = addStockMovementNoteRef.current?.value?.trim();

    if(!itemId){
      toast.error(t('inventory.toast_invalid_request'));
      return;
    }

    if(!movementType){
      toast.error(t('inventory.toast_select_movement_type'));
      return;
    }

    if (isNaN(quantity) || quantity <= 0) {
      toast.error(t('inventory.toast_invalid_quantity'));
      return;
    }

    try {
      const res = await addInventoryItemStockMovement({
        id: itemId,
        movementType,
        quantity,
        note
      });

      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);

        if (addStockMovementTypeRef.current) addStockMovementTypeRef.current.value = 'IN';
        if (addStockMovementItemIdRef?.current) {
          addStockMovementItemIdRef.current.value = "";
        }
        if (addStockMovementQuantityRef.current) addStockMovementQuantityRef.current.value = "";
        if (addStockMovementNoteRef.current) addStockMovementNoteRef.current.value = "";

        setIsMovementModalOpen(false);
        try { document.getElementById("modal-add-inventory-stock-movement")?.close?.(); } catch (e) {}

        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('inventory.toast_default_error');
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  const handleBulkAddStock = async () => {
    const quantity = parseFloat(String(bulkStockQuantityRef.current?.value || "0").replace(/,/g, ""));
    const note = bulkStockNoteRef.current?.value?.trim();

    if (isNaN(quantity) || quantity <= 0) {
      toast.error(t('inventory.toast_invalid_quantity') || "Invalid quantity");
      return;
    }

    try {
      const res = await addBulkStockToAllItems({ quantity, note });
      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);
        if (bulkStockQuantityRef.current) bulkStockQuantityRef.current.value = "";
        if (bulkStockNoteRef.current) bulkStockNoteRef.current.value = "";

        setIsBulkAddModalOpen(false);
        try { document.getElementById("modal-bulk-add-stock")?.close?.(); } catch (e) {}
        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('inventory.toast_default_error') || "Error adding bulk stock";
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  const handleBulkSetMinStock = async () => {
    const min_quantity = parseFloat(String(bulkMinStockRef.current?.value || "0").replace(/,/g, ""));

    if (isNaN(min_quantity) || min_quantity < 0) {
      toast.error("Invalid minimum quantity");
      return;
    }

    try {
      const res = await setBulkMinStockToAllItems({ min_quantity });
      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);
        if (bulkMinStockRef.current) bulkMinStockRef.current.value = "";

        setIsBulkMinStockModalOpen(false);
        try { document.getElementById("modal-bulk-set-min-stock")?.close?.(); } catch (e) {}
        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || "Error setting minimum stock";
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  // Close active dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.inventory-action-dropdown')) {
        setActiveActionMenuId(null);
      }
    };
    if (activeActionMenuId !== null) {
      document.addEventListener('click', handleOutsideClick);
      return () => document.removeEventListener('click', handleOutsideClick);
    }
  }, [activeActionMenuId]);

  // Reset pagination on search or status change
  useEffect(() => {
    setPage(1);
    setActiveActionMenuId(null);
  }, [state.search, selectedStatus]);

  // Filter and paginate inventory items
  const filteredInventory = (state.inventory || []).filter((item) => {
    if (!state.search) return true;
    return item.title?.toLowerCase().includes(state.search.trim().toLowerCase());
  });

  const totalPages = Math.max(1, Math.ceil(filteredInventory.length / itemsPerPage));
  const currentPage = Math.max(1, Math.min(page, totalPages));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInventory = filteredInventory.slice(startIndex, startIndex + itemsPerPage);

  const btnPaginationFirstPage = () => setPage(1);
  const btnPaginationPreviousPage = () => setPage(p => Math.max(1, p - 1));
  const btnPaginationNextPage = () => setPage(p => Math.min(totalPages, p + 1));
  const btnPaginationLastPage = () => setPage(totalPages);

  if (isLoading) {
    return <Page>{t('inventory.loading_text')}</Page>;
  }

  if (error) {
    return <Page>{t('inventory.error_loading_text')}</Page>;
  }

  return (
    <Page>
      <div className="flex flex-wrap gap-4 flex-col md:flex-row items-center justify-between">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <h3 className="text-2xl font-bold">{t('inventory.title')}</h3>
          <Link
            to="dashboard"
            className='rounded-lg border-none transition-colors duration-150 text-restro-text px-3 py-1.5 flex items-center gap-1 bg-restro-bg-gray hover:bg-restro-button-hover text-sm font-medium'
          >
            <IconCategory2 stroke={iconStroke} size={18} /> {t('inventory.dashboard')}
          </Link>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="flex items-center justify-between">
            <div className="relative md:w-72">
              <IconSearch className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                className="input input-sm input-bordered focus:outline-none focus:ring-1 focus:ring-emerald-500 pl-8 pr-2 w-full rounded-lg text-gray-700 dark:text-gray-200 h-8"
                placeholder={t('inventory.search_placeholder')}
                value={state.search}
                onChange={(e) => {
                  setState({
                    ...state,
                    search: e.target.value,
                  });
                }}
              />
            </div>
          </div>
          <button 
            type="button"
            onClick={() => setIsAddModalOpen(true)}  
            className="text-sm rounded-lg border transition-colors duration-150 px-2.5 py-1.5 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
          >
            <IconPlus size={18} stroke={iconStroke}/>
            {t('inventory.add_stock_item')}
          </button>
          <button 
            type="button"
            onClick={() => setIsBulkAddModalOpen(true)}  
            className="text-sm rounded-lg border transition-colors duration-150 px-2.5 py-1.5 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
          >
            <IconPlus size={18} stroke={iconStroke}/>
            Bulk Add Stock
          </button>
          <button 
            type="button"
            onClick={() => setIsBulkMinStockModalOpen(true)}  
            className="text-sm rounded-lg border transition-colors duration-150 px-2.5 py-1.5 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
          >
            <IconPlus size={18} stroke={iconStroke}/>
            Bulk Set Min Stock
          </button>

          <Link
            to="import"
            className="text-sm rounded-lg border transition-colors duration-150 px-2.5 py-1.5 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
          >
            {t('common.import')}
          </Link>
          <Link
            to="stock-movements"
            className='rounded-lg border-none transition-colors duration-150 text-restro-text px-3 py-1.5 flex items-center gap-1 bg-restro-bg-gray hover:bg-restro-button-hover text-sm font-medium'
          >
            <IconLogs stroke={iconStroke} size={18} /> {t('inventory.stock_movements')}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        {[
          {
            label: t('inventory.status_all'),
            icon: <IconBox stroke={1.5} className="text-gray-600" />,
            status: "all",
            count: state.inventory?.length || 0,
            border: "border-l-gray-400",
            countColor: "text-gray-600",
          },
          {
            label: t('inventory.status_in'),
            icon: <IconCarrot stroke={1.5} className="text-green-600" />,
            status: "in",
            count: state.statusCounts?.in,
            border: "border-l-restro-green",
            countColor: "text-restro-green",
          },
          {
            label: t('inventory.status_low'),
            icon: <IconAlertTriangle stroke={1.5} className="text-yellow-600" />,
            status: "low",
            count: state.statusCounts?.low,
            border: "border-l-yellow-600",
            countColor: "text-yellow-600",
          },
          {
            label: t('inventory.status_out'),
            icon: <IconCircleMinus stroke={1.5} className="text-red-600"/>,
            status: "out",
            count: state.statusCounts?.out,
            border: "border-l-red-600",
            countColor: "text-red-600",
          },
        ].map(({ label, icon, status, count, border, countColor }) => {
          const isSelected = selectedStatus === status;
          return (
            <div
              key={status}
              onClick={() => setSelectedStatus(status)}
              className={clsx(
                "cursor-pointer flex items-center gap-4 px-4 py-4 rounded-xl border border-l-8 transition-colors duration-150",
                border,
                isSelected
                  ? theme === 'black'
                    ? "bg-restro-card-iconbg/80 border-restro-border-dark-mode"
                    : "border-restro-green-light bg-gray-50"
                  : theme === 'black'
                    ? " border-restro-border-dark-mode"
                    : "border-restro-green-light"
              )}
            >
              <div className='p-3 rounded-lg bg-restro-card-bg'>{icon}</div>
              <div>
                <div className={`text-sm font-medium ${theme === 'black' ? 'text-white' : 'text-gray-600'} `}>{label}</div>
                <div className={`text-lg font-bold ${countColor}`}>{count}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="h-[calc(100vh-310px)] overflow-auto w-[100%] mt-4 rounded-xl border border-gray-100 dark:border-zinc-800">
       {filteredInventory.length === 0 ? (
          <div className="flex flex-col justify-center items-center text-center h-full p-8">
            <img
              src="/assets/illustrations/orders-not-found.webp"
              alt={t('inventory.no_items_image_alt')}
              className="w-1/2 md:w-60"
            />
            <p className='text-md text-restro-text mt-4 font-medium'>{t('inventory.no_items_message')}</p>
          </div>
        ) :
        (<table className="table table-sm w-full">
          <thead className='border-b sticky top-0 z-10 bg-restro-bg-gray border-restro-border-green'>
            <tr>
              <th className="text-start p-2.5">#</th>
              <th className="text-start">{t('inventory.table_item')}</th>
              <th className="text-start">{t('inventory.table_current_stock')}</th>
              <th className="text-start">{t('inventory.table_min_stock')}</th>
              <th className="text-start">{t('inventory.table_stock_status')}</th>
              <th className="text-start max-w-20">{t('inventory.table_updated_on')}</th>
              <th className="text-start w-16">{t('inventory.table_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {
              paginatedInventory.map((inventoryItem, i) => {
                const { id, title, quantity, unit, min_quantity_threshold, status, updated_at } = inventoryItem;
                const isMenuOpen = activeActionMenuId === id;
                return (
                  <tr key={id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30">
                    <td>{filteredInventory.length - (startIndex + i)}</td>
                    <td>
                      <p className="text-ellipsis line-clamp-2 font-medium">{title}</p>
                    </td>
                    <td>
                      <p>
                        {quantity ? quantity : "-"} {unit}
                      </p>
                    </td>
                    <td>
                      <p>
                        {min_quantity_threshold ? min_quantity_threshold : "-"} {unit}
                      </p>
                    </td>
                    <td>
                      <span
                        className={`text-xs rounded-lg py-1 px-2 font-semibold
                          ${status === 'in' ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300' : ''}
                          ${status === 'low' ? 'bg-yellow-100 dark:bg-yellow-600 text-yellow-600 dark:text-yellow-300' : ''}
                          ${status === 'out' ? 'bg-red-100 dark:bg-red-700 text-red-600 dark:text-red-200' : ''}
                        `}
                      >
                        {status === 'in' ? t('inventory.stock_status_in') :
                        status === 'low' ? t('inventory.stock_status_low') :
                        status === 'out' ? t('inventory.stock_status_out') : '-'}
                      </span>
                    </td>
                    <td>
                      {updated_at
                        ? moment.utc(updated_at).local().format("DD/MM/YYYY, h:mm A")
                        : "-"}
                    </td>
                    <td>
                      <div className="relative inventory-action-dropdown">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveActionMenuId(isMenuOpen ? null : id);
                          }}
                          className='text-restro-text hover:bg-restro-button-hover p-1.5 rounded-lg transition-colors duration-100'
                          title={t('inventory.table_actions')}
                        >
                          <IconDotsVertical size={18} stroke={iconStroke} />
                        </button>
                        {isMenuOpen && (
                          <div 
                            className='absolute right-0 top-full mt-1 bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-700 shadow-xl w-56 z-50 p-1.5'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setActiveActionMenuId(null);
                                setIsMovementModalOpen(true);
                                setTimeout(() => {
                                  if (addStockMovementItemIdRef?.current) {
                                    addStockMovementItemIdRef.current.value = inventoryItem.id;
                                  }
                                }, 50);
                              }}
                              className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                            >
                              <IconPlus size={16} stroke={iconStroke} /> {t('inventory.dropdown_add_stock_movement')}
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setSelectedUpdateItem(inventoryItem.id);
                                setActiveActionMenuId(null);
                                setIsUpdateModalOpen(true);
                                setTimeout(() => {
                                  if (updateTitleRef.current) updateTitleRef.current.value = inventoryItem.title || "";
                                  if (updateQuantityRef.current) updateQuantityRef.current.value = inventoryItem.quantity || "";
                                  if (updateUnitRef.current) updateUnitRef.current.value = inventoryItem.unit || "pc";
                                  if (updateMinQuantityThresholdRef.current) updateMinQuantityThresholdRef.current.value = inventoryItem.min_quantity_threshold || "";
                                }, 50);
                              }}
                              className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                            >
                              <IconPencil size={16} stroke={iconStroke} /> {t('inventory.dropdown_update_item')}
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setSelectedDeleteItem(inventoryItem?.id || null);
                                setActiveActionMenuId(null);
                                setIsDeleteModalOpen(true);
                              }}
                              className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                            >
                              <IconTrash size={16} stroke={iconStroke} /> {t('inventory.dropdown_delete_item')}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>)}
      </div>

      {/* Pagination Controls */}
      {filteredInventory.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-4 px-2">
          <span className="text-xs text-gray-500 font-medium">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredInventory.length)} of {filteredInventory.length} items
          </span>
          <div className="join">
            <button
              type="button"
              onClick={btnPaginationFirstPage}
              disabled={currentPage === 1}
              className="join-item btn btn-xs px-2.5 rounded-l-lg border border-gray-300 dark:border-zinc-700 disabled:opacity-40"
              title="First Page"
            >
              <IconChevronsLeft size={16} stroke={iconStroke} />
            </button>
            <button
              type="button"
              onClick={btnPaginationPreviousPage}
              disabled={currentPage === 1}
              className="join-item btn btn-xs px-2.5 border border-gray-300 dark:border-zinc-700 disabled:opacity-40"
              title="Previous Page"
            >
              <IconChevronLeft size={16} stroke={iconStroke} />
            </button>
            <button
              type="button"
              className="join-item btn btn-xs px-4 border border-gray-300 dark:border-zinc-700 font-bold pointer-events-none"
            >
              Page {currentPage} of {totalPages}
            </button>
            <button
              type="button"
              onClick={btnPaginationNextPage}
              disabled={currentPage === totalPages}
              className="join-item btn btn-xs px-2.5 border border-gray-300 dark:border-zinc-700 disabled:opacity-40"
              title="Next Page"
            >
              <IconChevronRight size={16} stroke={iconStroke} />
            </button>
            <button
              type="button"
              onClick={btnPaginationLastPage}
              disabled={currentPage === totalPages}
              className="join-item btn btn-xs px-2.5 rounded-r-lg border border-gray-300 dark:border-zinc-700 disabled:opacity-40"
              title="Last Page"
            >
              <IconChevronsRight size={16} stroke={iconStroke} />
            </button>
          </div>
        </div>
      )}

      {/* dialog: add stock items */}    
      {isAddModalOpen && (
        <dialog id="modal-add-inventory-item" open={true} className="modal modal-open modal-bottom sm:modal-middle">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <div className='flex justify-between items-center'>
              <h3 className="font-bold text-lg">{t('inventory.modal_add_title')}</h3>
              <button 
                type="button"
                className='text-red-500 p-2 rounded-full bg-restro-bg-gray hover:bg-restro-button-hover'  
                onClick={() => setIsAddModalOpen(false)}
              >
                <IconX size={18} stroke={iconStroke}/>
              </button>
            </div>

            {/* Title */}
            <div className="mt-4">
              <label htmlFor="item_title" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_item_name_label')}</label>
              <input ref={titleRef} type="text"  className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_item_name_placeholder')} />
            </div>

            <div className="mt-4 flex items-end gap-4">
              <div className="flex-1">
                <label htmlFor="item_unit" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_item_unit_label')}</label>
                <select ref={unitRef} defaultValue="pc" className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green'>
                  <option value="pc">{t('inventory.form_item_unit_pc')}</option>
                  <option value="kg">{t('inventory.form_item_unit_kg')}</option>
                  <option value="g">{t('inventory.form_item_unit_g')}</option>
                  <option value="l">{t('inventory.form_item_unit_l')}</option>
                  <option value="ml">{t('inventory.form_item_unit_ml')}</option>
                </select>
              </div>
            </div>

            {/* Current Qty and Min Qty */}
            <div className="mt-4 flex items-center gap-4">
              <div className="flex-1">
                <label htmlFor="item_qty" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_item_qty_label')}</label>
                <input ref={quantityRef} type="number"  className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_item_qty_placeholder')} />
              </div>
              <div className="flex-1">
                <label htmlFor="low_stock_qty" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_item_min_qty_label')}</label>
                <input ref={minQuantityThresholdRef} type="number" className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_item_min_qty_placeholder')} />
              </div>
            </div>

            {/* Actions */}
            <div className="modal-action w-full mt-6">
              <button
                type="button"
                onClick={handleAddInventoryItem}
                className='flex justify-center w-full rounded-xl transition-colors duration-150 px-4 py-3 text-white border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover font-bold'
              >
                {t('inventory.form_save_button')}
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* dialog: update item */}
      {isUpdateModalOpen && (
        <dialog id="modal-update-inventory-item" open={true} className="modal modal-open modal-bottom sm:modal-middle">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <div className='flex justify-between items-center'>
              <h3 className="font-bold text-lg">{t('inventory.modal_update_title')}</h3>
              <button 
                type="button"
                className='text-red-600 p-2 rounded-full bg-restro-bg-gray hover:bg-restro-button-hover' 
                onClick={() => {
                  setIsUpdateModalOpen(false);
                  setSelectedUpdateItem(null);
                }}
              >
                <IconX size={18} stroke={iconStroke}/>
              </button>
            </div>

            {/* Title */}
            <div className="mt-4">
              <label htmlFor="item_title" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_update_item_name_label')}</label>
              <input ref={updateTitleRef} type="text" className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_item_name_placeholder')} />
            </div>

            <div className="mt-4 flex items-end gap-4">
              <div className="flex-1">
                <label htmlFor="item_unit" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_update_unit_label')}</label>
                <select ref={updateUnitRef} disabled className='disabled:cursor-not-allowed text-sm w-full border rounded-lg px-4 py-2 border-restro-border-green dark:bg-black focus:outline-restro-border-green' >
                  <option value="pc">{t('inventory.form_item_unit_pc')}</option>
                  <option value="kg">{t('inventory.form_item_unit_kg')}</option>
                  <option value="g">{t('inventory.form_item_unit_g')}</option>
                  <option value="l">{t('inventory.form_item_unit_l')}</option>
                  <option value="ml">{t('inventory.form_item_unit_ml')}</option>
                </select>
              </div>
            </div>

            {/* Current Qty and Min Qty */}
            <div className="mt-4 flex items-center gap-4">
              <div className="flex-1">
                <label htmlFor="item_qty" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_update_qty_label')}</label>
                <input ref={updateQuantityRef} type="number" disabled className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_update_qty_placeholder')} />
              </div>
              <div className="flex-1">
                <label htmlFor="low_stock_qty" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_update_min_qty_label')}</label>
                <input ref={updateMinQuantityThresholdRef} type="number" className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_update_min_qty_placeholder')} />
              </div>
            </div>

            {/* Actions */}
            <div className="modal-action w-full mt-6">
              <button
                type="button"
                onClick={handleUpdateInventoryItem}
                className='flex w-full justify-center rounded-xl transition-colors duration-150 px-4 py-3 text-white border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover font-bold'
              >
                {t('inventory.form_save_button')}
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* dialog: add stock movement */}
      {isMovementModalOpen && (
        <dialog id="modal-add-inventory-stock-movement" open={true} className="modal modal-open modal-bottom sm:modal-middle">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <div className='flex justify-between items-center'>
              <h3 className="font-bold text-lg">{t('inventory.modal_add_stock_movement_title')}</h3>
              <button 
                type="button"
                className='text-red-600 p-2 rounded-full hover:bg-restro-button-hover' 
                onClick={() => setIsMovementModalOpen(false)}
              >
                <IconX size={18} stroke={iconStroke}/>
              </button>
            </div>

            <div className="mt-4">
              <label htmlFor="movement_type" className="mb-1 block text-gray-500 text-sm">
                {t('inventory.modal_add_stock_movement_type')}
              </label>
              <select
                ref={addStockMovementTypeRef}
                name="movement_type"
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green'
                defaultValue="IN"
              >
                <option value="IN">{t('inventory.movement_type_in')}</option>
                <option value="OUT">{t('inventory.movement_type_out')}</option>
                <option value="WASTAGE">{t('inventory.movement_type_wastage')}</option>
              </select>
            </div>

            <div className="mt-4">
              <input type="hidden" ref={addStockMovementItemIdRef} />
              <label htmlFor="add_quantity" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_add_stock_qty_label')}</label>
              <input
                ref={addStockMovementQuantityRef}
                type="number"
                name="add_quantity"
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' 
                placeholder={t('inventory.form_add_stock_qty_placeholder')}
              />
            </div>

            <div className="mt-4">
              <label htmlFor="add_notes" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_add_stock_notes_label')}</label>
              <textarea
                ref={addStockMovementNoteRef}
                name="add_notes"
                rows={4}
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green resize-none'
                placeholder={t('inventory.form_add_stock_notes_placeholder')}
              />
            </div>

            <div className="modal-action w-full mt-6">
              <button
                type="button"
                onClick={handleAddItemStockMovement}
                className='rounded-xl transition-colors duration-150 px-4 py-3 text-white w-full border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover font-bold'
              >
                {t('inventory.form_save_button')}
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* dialog: delete inventory item */}
      {isDeleteModalOpen && (
        <dialog id="modal-delete-inventory-item" open={true} className="modal modal-open">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <h3 className="font-bold text-lg">{t('inventory.modal_delete_title')}</h3>
            <p className="py-4 text-gray-600 dark:text-gray-300">
              {t('inventory.modal_delete_confirm')}
            </p>
            <div className="modal-action">
              <button 
                type="button"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setSelectedDeleteItem(null);
                }}
                className='btn btn-sm px-4 rounded-xl border border-gray-300 dark:border-zinc-700 bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-200'
              >
                {t('inventory.modal_delete_cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteInventoryItem}
                className="ml-2 btn btn-sm rounded-xl hover:bg-red-700 bg-red-600 text-white font-bold"
              >
                {t('inventory.modal_delete_confirm_button')}
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* dialog: bulk add stock */}
      {isBulkAddModalOpen && (
        <dialog id="modal-bulk-add-stock" open={true} className="modal modal-open modal-bottom sm:modal-middle">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <div className='flex justify-between items-center'>
              <h3 className="font-bold text-lg">Add Stock to ALL Inventory Items</h3>
              <button 
                type="button"
                className='text-red-600 p-2 rounded-full hover:bg-restro-button-hover' 
                onClick={() => setIsBulkAddModalOpen(false)}
              >
                <IconX size={18} stroke={iconStroke}/>
              </button>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-4">This will instantly increase the stock quantity of every single inventory item in the system.</p>
              <label htmlFor="bulk_add_quantity" className="mb-1 block text-gray-500 text-sm">Quantity to add to ALL items:</label>
              <input
                ref={bulkStockQuantityRef}
                type="number"
                name="bulk_add_quantity"
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' 
                placeholder="e.g. 1000"
              />
            </div>
            <div className="mt-4">
              <label htmlFor="bulk_add_notes" className="mb-1 block text-gray-500 text-sm">Notes (Optional):</label>
              <textarea
                ref={bulkStockNoteRef}
                name="bulk_add_notes"
                rows={2}
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green resize-none'
                placeholder="e.g. Bulk Advance Upload"
              />
            </div>
            <div className="modal-action w-full mt-6">
              <button
                type="button"
                onClick={handleBulkAddStock}
                className='rounded-xl transition-colors duration-150 px-4 py-3 text-white w-full border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover font-bold'
              >
                Increase Stock of All Items
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* dialog: bulk set min stock */}
      {isBulkMinStockModalOpen && (
        <dialog id="modal-bulk-set-min-stock" open={true} className="modal modal-open modal-bottom sm:modal-middle">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <div className='flex justify-between items-center'>
              <h3 className="font-bold text-lg">Set Min. Stock for ALL Items</h3>
              <button 
                type="button"
                className='text-red-600 p-2 rounded-full hover:bg-restro-button-hover' 
                onClick={() => setIsBulkMinStockModalOpen(false)}
              >
                <IconX size={18} stroke={iconStroke}/>
              </button>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-4">This will set the Minimum Quantity Threshold for every single inventory item in the system. Items with quantity below this threshold will be marked as "Low Stock".</p>
              <label htmlFor="bulk_min_quantity" className="mb-1 block text-gray-500 text-sm">Minimum Stock Quantity:</label>
              <input
                ref={bulkMinStockRef}
                type="number"
                name="bulk_min_quantity"
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' 
                placeholder="e.g. 5"
              />
            </div>
            <div className="modal-action w-full mt-6">
              <button
                type="button"
                onClick={handleBulkSetMinStock}
                className='rounded-xl transition-colors duration-150 px-4 py-3 text-white w-full border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover font-bold'
              >
                Set Minimum Stock for All
              </button>
            </div>
          </div>
        </dialog>
      )}

    </Page>
  );
}
